import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { supabase } from "../lib/supabase";

const SERVER_TIME_ERROR = "The document could not be stamped because server time is unavailable. Apply the latest Supabase migration and try again.";

export const getServerTimestamp = async () => {
  const { data, error } = await supabase.rpc("get_server_timestamp");
  if (error || !data) throw new Error(SERVER_TIME_ERROR);
  const value = typeof data === "object"
    ? data.server_timestamp || data.timestamp || data.now
    : data;
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) throw new Error(SERVER_TIME_ERROR);
  return timestamp.toISOString();
};

export const nextPaint = () => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

export const formatServerDateTime = value => {
  if (!value) return "Server time unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Server time unavailable";
  return new Intl.DateTimeFormat("en-KE", {
    timeZone: "Africa/Nairobi",
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(date);
};

const qrText = value => String(value ?? "").replace(/\s+/g, " ").trim();

const qrLabel = key => String(key || "")
  .replace(/_/g, " ")
  .replace(/\b\w/g, character => character.toUpperCase());

export const createDocumentQrValue = ({ type, documentId, student, generatedAt, extra = {} }) => {
  const studentName = [student?.first_name, student?.middle_name, student?.last_name]
    .filter(Boolean)
    .join(" ") || student?.full_name;
  const registrationNumber = student?.admission_number || student?.registration_number || student?.reg_number;
  const programmeName = student?.program?.name || student?.programme?.name || student?.program_name;
  const programmeCode = student?.program?.code || student?.programme?.code || student?.program_code;
  const documentName = extra.document || String(type || "document").replace(/_/g, " ");
  const lines = [
    "COLLEGE MANAGEMENT SYSTEM",
    "QR DOCUMENT VERIFICATION",
    `DOCUMENT: ${qrText(documentName) || "Document"}`,
    `DOCUMENT NO: ${qrText(documentId) || "Not provided"}`,
    `STUDENT NAME: ${qrText(studentName) || "Not provided"}`,
    `REGISTRATION NO: ${qrText(registrationNumber) || "Not provided"}`,
    `PROGRAMME: ${qrText(programmeName) || "Not provided"}`,
    `PROGRAMME CODE: ${qrText(programmeCode) || "Not provided"}`,
    `LEVEL: ${qrText(student?.academic_level) || "Not provided"}`,
    `MODULE: ${qrText(student?.current_module || student?.starting_module) || "Not provided"}`,
    `INTAKE: ${qrText(student?.intake_code) || "Not provided"}`,
    `STATUS: ${qrText(student?.status || student?.enrollment_status) || "Not provided"}`,
    `GENERATED: ${formatServerDateTime(generatedAt)}`,
    "TIME SOURCE: DATABASE SERVER (Africa/Nairobi)",
    "VERIFY: Compare these values with the document content.",
  ];

  Object.entries(extra)
    .filter(([key]) => key !== "document" && extra[key] != null && extra[key] !== "")
    .forEach(([key, value]) => {
      const renderedValue = typeof value === "object" ? JSON.stringify(value) : value;
      lines.push(`${qrLabel(key).toUpperCase()}: ${qrText(renderedValue)}`);
    });

  return lines.join("\n");
};

const canvasSlice = (source, offset, height) => {
  const slice = document.createElement("canvas");
  slice.width = source.width;
  slice.height = height;
  const context = slice.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, slice.width, slice.height);
  context.drawImage(source, 0, offset, source.width, height, 0, 0, source.width, height);
  return slice;
};

export const downloadHtmlAsPdf = async ({ element, filename, orientation = "portrait", margin = 8, title }) => {
  if (!element) throw new Error("The document is not ready for download.");
  const sourceWidth = Math.max(element.scrollWidth, element.clientWidth, element.getBoundingClientRect().width);
  let captureElement = element;
  let captureClone = null;

  // Export the complete document, not the viewport-sized/scrollable version
  // used by the dashboard. This prevents long sections from being clipped in
  // the downloaded PDF while leaving the on-screen layout unchanged.
  captureClone = element.cloneNode(true);
  captureClone.style.position = "absolute";
  captureClone.style.left = "-100000px";
  captureClone.style.top = "0";
  captureClone.style.width = `${sourceWidth}px`;
  captureClone.style.maxWidth = "none";
  captureClone.style.height = "auto";
  captureClone.style.maxHeight = "none";
  captureClone.style.overflow = "visible";
  captureClone.style.backgroundColor = "#ffffff";
  document.body.appendChild(captureClone);

  captureClone.querySelectorAll("*").forEach(node => {
    const computed = window.getComputedStyle(node);
    if (computed.overflow !== "visible" || computed.overflowY !== "visible") node.style.overflow = "visible";
    if (computed.maxHeight !== "none") node.style.maxHeight = "none";
    if (node.scrollHeight > node.clientHeight && computed.height !== "auto") node.style.height = "auto";
  });

  captureElement = captureClone;
  try {
    await nextPaint();
    const canvas = await html2canvas(captureElement, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
    width: captureElement.scrollWidth,
    height: captureElement.scrollHeight,
    windowWidth: Math.max(document.documentElement.clientWidth, captureElement.scrollWidth),
    windowHeight: Math.max(document.documentElement.clientHeight, captureElement.scrollHeight),
    });

    const pdf = new jsPDF({ orientation, unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    const contentHeight = pageHeight - margin * 2;
    const sliceHeightPixels = Math.max(1, Math.floor((contentHeight / contentWidth) * canvas.width));
    let offset = 0;
    let page = 0;

    while (offset < canvas.height) {
      if (page > 0) pdf.addPage();
      const height = Math.min(sliceHeightPixels, canvas.height - offset);
      const slice = canvasSlice(canvas, offset, height);
      const imageHeight = (height * contentWidth) / canvas.width;
      pdf.addImage(slice.toDataURL("image/png"), "PNG", margin, margin, contentWidth, imageHeight, undefined, "FAST");
      offset += height;
      page += 1;
    }

    if (title) pdf.setProperties({ title, creator: "College Management System" });
    pdf.save(filename);
  } finally {
    captureClone?.remove();
  }
};

